import { lazy } from 'react';

const RELOAD_FLAG = '__kaiyloLazyChunkReload';

export function isStaleChunkError(error) {
  const message = String(error?.message ?? error ?? '');
  return (
    (message.includes('text/html') && message.includes('MIME')) ||
    // Chrome / Edge
    /Failed to fetch dynamically imported module/i.test(message) ||
    // Firefox
    /error loading dynamically imported module/i.test(message) ||
    // Safari
    /Importing a module script failed/i.test(message) ||
    /Loading chunk [\d]+ failed/i.test(message)
  );
}

// Le drapeau sur window disparaît avec le rechargement : on garde aussi l'heure
// du dernier rechargement dans sessionStorage, pour ne pas boucler si le chunk
// reste introuvable sur la nouvelle version (réseau coupé, déploiement cassé).
const LAST_RELOAD_KEY = 'kaiylo:lazyChunkReloadAt';
const RELOAD_COOLDOWN_MS = 30_000;

function reloadedRecently() {
  try {
    const at = Number(window.sessionStorage.getItem(LAST_RELOAD_KEY));
    return Number.isFinite(at) && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReload() {
  try {
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    // Stockage indisponible (navigation privée) : le drapeau window suffit.
  }
}

/**
 * React.lazy wrapper: on stale-build chunk failures (HTML instead of JS), reload once
 * so the browser fetches a fresh index with correct hashed asset URLs.
 */
export function lazyImport(importFn) {
  return lazy(withStaleChunkReload(importFn));
}

export function withStaleChunkReload(importFn) {
  return async () => {
    try {
      return await importFn();
    } catch (error) {
      if (
        typeof window !== 'undefined' &&
        isStaleChunkError(error) &&
        !window[RELOAD_FLAG] &&
        !reloadedRecently()
      ) {
        window[RELOAD_FLAG] = true;
        markReload();
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }
  };
}
